<template lang="pug">
  .schedule__device(
    :class="{close: !showSchedules, notReady: loading}")

    //- Fila con el nombre del dispostivio
    .units__device__bar
      .units__device__name(@click="toggleSchedules")
        van-icon.media__figure(:name="GetDeviceIcon(device, ACSMode)")
        | {{ device.name }}
      .units__device__action(v-if="schedules && !waterDevice" @click="toggleSchedules")
        van-icon(name="down")
      .units__device__message(v-if="waterDevice")
        | {{ $t("schedule.taiConfiguration") }},<br> {{ $t("schedule.schedulesUnsopported") }}
      .units__device__errors(v-if="!loading") {{ $t('home.notConnected') }}
      .units__device__loading(v-if="loading")
        van-loading(type="spinner")

    //- Fila con la barra con las programaciones
    .schedule__device__graph(v-if="!waterDevice")
      .schedule__device__graph__titles(v-if="hour24")
        .schedule__device__graph__title
          span 00
        .schedule__device__graph__title
          span 06
        .schedule__device__graph__title
          span 12
        .schedule__device__graph__title
          span 18
        .schedule__device__graph__title
          span 24

      .schedule__device__graph__titles(v-if="!hour24")
        .schedule__device__graph__title
          span 12
            span AM
        .schedule__device__graph__title
          span 06
            span AM
        .schedule__device__graph__title
          span 12
            span PM
        .schedule__device__graph__title
          span 06
            span PM
        .schedule__device__graph__title
          span 12
            span AM

      .schedule__device__graph__bar
        .schedule__device__graph__bar__item(
          v-for="bar in schedulesBar"
          :class="GetModeIcon(bar.color)"
          :style="{left: bar.left, width: bar.width}")

    //- Listado de dispositivos
    .schedule__devices(v-if="schedules && !waterDevice")
      .schedule__item(
        v-for="schedule in schedules"
        :key="schedule.id"
        :class="{disabled: !schedule.enabled}"
        @click="updateSchedule(installationID, schedule)")
        .schedule__item__time(v-html="getTime(schedule.hour, schedule.minutes)")
        .schedule__item__action
          .schedule__item__name {{ schedule.name }}
          .schedule__item__data
            span.schedule__item__action__state {{ parsePower(schedule.power) }}
            van-icon(:name="GetModeIcon(schedule.mode)" v-if="schedule.mode && schedule.power")
            span(v-if="schedule.power && schedule.mode <= 3 && schedule.setpoint")
              span.schedule__item__action__temp {{ schedule.setpoint }}
              span.schedule__item__action__unitType {{ tempUnit }}
            .float-right(v-if="!schedule.enabled") {{ $t('new.schedule.disabledDevice') }}

</template>

<script>
import TimeMixin from '@/mixins/Time.mixin';
import GetDeviceIcon from '@/mixins/GetDeviceIcon.mixin';
import GetModeIcon from '@/mixins/GetModeIcon.mixin';
import { CONFIG, TAI } from '@/app.config';
import { UnitFilter } from '@/filters/Temp.filter';
import { mapGetters } from 'vuex';
import { hasProp } from '../../utils/validate.utils';

export default {
  mixins: [TimeMixin, GetDeviceIcon, GetModeIcon],
  filters: { UnitFilter },
  props: {
    /**
     * Mac del dispositivo
     */
    mac: {
      type: String,
      required: true,
    },
    /**
     * ID de la instalación a la que pertenece el dispositivo
     */
    installationID: {
      type: String,
      required: true,
    },
    /**
     * ID de la instalación a la que pertenece el dispositivo
     */
    deviceSchedules: {
      type: Object,
      default: null,
    },
    /**
     * Día de la semana seleccionado
     */
    day: {
      type: Number,
      required: true,
    },
    /**
     * Indica si se visualiza la línea como ACS
     */
    ACSMode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      showSchedules: false,
      hour24: CONFIG.HOUR24,
    };
  },

  computed: {
    ...mapGetters({
      getDevice: 'unitsModule/getDevice',
    }),

    /**
     * Devuelve los datos del dispositivo almacenados en el Store
     *
     * @return {Device} - Datos del dispositivo
     */
    device() {
      return this.getDevice(this.installationID, this.mac);
    },

    /**
     * Indica si la unidad es de agua
     */
    waterDevice() {
      return this.device.acs_available && this.device.tai_th === TAI.WATER && !this.ACSMode;
    },

    /**
     * Al loguearse un usuario, recibe unos datos mínimos de cada dispositivo,
     * pero la mayoría de los datos los recibe tras conectarse a los sockets,
     * esta propiedad indica si estos datos se han recibido o no (loading)
     *
     * @return {Boolean} - Indica si se han recibido los datos o no
     */
    loading() {
      if (!this.device) return true;
      return !Object.prototype.hasOwnProperty.call(this.device, 'isConnected');
    },

    /**
     * Devuelve la unidad de temperatura
     */
    tempUnit() {
      return this.$options.filters.UnitFilter(this.device.units);
    },

    /**
     * Devuelve todas las programaciones de un dia
     */
    schedules() {
      if (!this.device || !this.deviceSchedules) return null;

      if (
        this.ACSMode &&
        hasProp(this.deviceSchedules, 'schedulesACS') &&
        hasProp(this.deviceSchedules.schedulesACS, this.day)
      ) {
        return this.deviceSchedules.schedulesACS[this.day];
      }

      if (
        !this.ACSMode &&
        hasProp(this.deviceSchedules, 'schedules') &&
        hasProp(this.deviceSchedules.schedules, this.day)
      ) {
        return this.deviceSchedules.schedules[this.day];
      }

      return null;
    },

    /*
     * Genera las barra de programación de la semana
     */
    schedulesBar() {
      if (!this.device || !this.deviceSchedules) return null;

      if (
        this.ACSMode &&
        hasProp(this.deviceSchedules, 'schedulesBarACS') &&
        hasProp(this.deviceSchedules.schedulesBarACS, this.day)
      )
        return this.deviceSchedules.schedulesBarACS[this.day];

      if (
        !this.ACSMode &&
        hasProp(this.deviceSchedules, 'schedulesBar') &&
        hasProp(this.deviceSchedules.schedulesBar, this.day)
      )
        return this.deviceSchedules.schedulesBar[this.day];

      return null;
    },
  },

  methods: {
    updateSchedule(installationID, schedule) {
      this.$emit('updateSchedule', { installationID, schedule, ACSMode: this.ACSMode });
    },

    /**
     * Muestra/Oculta el listado de dispositivos
     */
    toggleSchedules() {
      if (this.schedules) {
        this.showSchedules = !this.showSchedules;
      }
    },

    /**
     * Devuelve una cadena indicando el estado que define la programación
     */
    parsePower(status) {
      return status ? 'ON' : 'OFF';
    },
  },
};
</script>
